import { Checkbox } from "@/components/ui/checkbox";
import { ShieldAlert, Tag, Hash, Lock } from "lucide-react";

// Is this number the assembly's OWN identity (its primary/OEM)?
export function isIdentityNumber(n, primaryNorm, primaryValue) {
  if (!n) return false;
  if (n.owner === "truck_oem") return true;
  if (primaryNorm && n.normalized && n.normalized === primaryNorm) return true;
  if (primaryValue && n.value && String(n.value).toUpperCase() === String(primaryValue).toUpperCase()) return true;
  return false;
}

// LOCKED GUARDRAIL: the assembly's OEM number(s) AND its superseded number(s) are its
// permanent identity — they can NEVER move to a child (no override). Mirrors the donor tie.
export function isProtectedNumber(n, primaryNorm, primaryValue, supersededNorms = []) {
  if (isIdentityNumber(n, primaryNorm, primaryValue)) return true;
  if (n?.normalized && (supersededNorms || []).includes(n.normalized)) return true;
  return false;
}

// Shared reference-selection UI for the number/keyword hand-off (Phase A spin-off AND Split).
// Fully controlled: the caller owns the Sets + toggle callbacks so both entry points behave
// identically (default MOVE, per-item "keep on both"). Protected identity numbers (OEM +
// superseded) are shown READ-ONLY — locked, never selectable, no override.
export function ReferencePicker({
  numbers = [], keywords = [], primaryNorm = null, primaryValue = null, supersededNorms = [], prefix = "spinoff",
  sel, onToggleNum, keepNum, onToggleKeepNum,
  selKw, onToggleKw, keepKw, onToggleKeepKw,
}) {
  const isProtected = (n) => isProtectedNumber(n, primaryNorm, primaryValue, supersededNorms);
  const componentNums = numbers.filter((n) => !isProtected(n));
  const protectedNums = numbers.filter(isProtected);

  // THE WHOLE TAG IN ONE TICK (2026-10-13 grouping). When a bolt-on comes off the assembly,
  // everything read off ITS tag goes with it — the part number, the serial, the date code. Ticking
  // five values one at a time is exactly the extra work he refuses, so the tag has its own tick.
  // IT IS SELECTION, NOT VERIFICATION: every value keeps whatever ✓verify state it already had.
  const tagGroups = [];
  componentNums.forEach((n) => {
    const key = n.group_id || "__assembly__";
    let g = tagGroups.find((x) => x.key === key);
    if (!g) {
      g = { key, label: n.group_label || "The assembly itself", rows: [] };
      tagGroups.push(g);
    }
    g.rows.push(n);
  });
  tagGroups.sort((a, b) => (a.key === "__assembly__" ? -1 : b.key === "__assembly__" ? 1 : 0));
  const grouped = tagGroups.length > 1;

  const numberRow = (n) => (
    <div key={n.id} data-testid={`${prefix}-num-${n.id}`} className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${sel.has(n.id) ? "border-[#E0781A] bg-orange-50" : "border-slate-200"}`}>
      <Checkbox data-testid={`${prefix}-num-check-${n.id}`} checked={sel.has(n.id)} onCheckedChange={() => onToggleNum(n.id)} className="border-slate-300" />
      <span className="font-mono font-bold">{n.value}</span>
      {n.group_primary && <span className="text-[10px] uppercase font-bold text-sky-700">this tag's part #</span>}
      {n.number_role && n.number_role !== "service_part" && <span className="text-[10px] text-slate-500">{n.number_role.replace("_", " ")}</span>}
      {n.brand && <span className="text-xs text-slate-500">{n.brand}</span>}
      {sel.has(n.id) && (
        <label className="ml-auto flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer">
          <Checkbox data-testid={`${prefix}-num-keep-${n.id}`} checked={keepNum.has(n.id)} onCheckedChange={() => onToggleKeepNum(n.id)} className="border-slate-300 h-3.5 w-3.5" />
          keep on both
        </label>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      {componentNums.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Hash className="w-3.5 h-3.5" />Numbers to move</div>
          {grouped
            ? tagGroups.map((g, gi) => {
              const ids = g.rows.map((n) => n.id);
              const all = ids.every((id) => sel.has(id));
              return (
                <div key={g.key} data-testid={`${prefix}-group-${gi}`} className="rounded-lg border border-slate-200">
                  <label className="flex items-center gap-2 px-2.5 py-1.5 bg-slate-50 cursor-pointer">
                    <Checkbox data-testid={`${prefix}-group-check-${gi}`} checked={all}
                      onCheckedChange={() => ids.forEach((id) => (all ? sel.has(id) : !sel.has(id)) && onToggleNum(id))}
                      className="border-slate-300" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">{g.label}</span>
                    <span className="text-[10px] text-slate-400">{g.rows.length} value{g.rows.length === 1 ? "" : "s"} — tick the whole tag</span>
                  </label>
                  <div className="p-1.5 space-y-1.5">{g.rows.map(numberRow)}</div>
                </div>
              );
            })
            : componentNums.map(numberRow)}
        </div>
      )}

      {protectedNums.length > 0 && (
        <div className="space-y-1.5" data-testid={`${prefix}-protected`}>
          <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" />Assembly's OEM / superseded number(s) — locked, never move</div>
          <div className="flex items-start gap-2 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5">
            <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            These are the assembly's permanent identity (like the donor tie). They stay on the assembly and can never move to a child.
          </div>
          {protectedNums.map((n) => (
            <div key={n.id} data-testid={`${prefix}-protected-num-${n.id}`} className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/50 px-2.5 py-2 opacity-90">
              <Lock className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span className="font-mono font-bold">{n.value}</span>
              <span className="text-[10px] uppercase font-bold text-emerald-700">{n.owner === "truck_oem" || (primaryNorm && n.normalized === primaryNorm) || (primaryValue && String(n.value).toUpperCase() === String(primaryValue).toUpperCase()) ? "OEM" : "superseded"}</span>
            </div>
          ))}
        </div>
      )}

      {keywords.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" />Keywords to move</div>
          <div className="flex flex-wrap gap-1.5">
            {keywords.map((k) => (
              <button key={k} type="button" data-testid={`${prefix}-kw-${k}`} onClick={() => onToggleKw(k)}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold border transition-colors ${selKw.has(k) ? "border-[#E0781A] bg-orange-50 text-[#B45309]" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                <Tag className="w-3 h-3" />{k}
              </button>
            ))}
          </div>
          {[...selKw].length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {[...selKw].map((k) => (
                <label key={k} className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer">
                  <Checkbox data-testid={`${prefix}-kw-keep-${k}`} checked={keepKw.has(k)} onCheckedChange={() => onToggleKeepKw(k)} className="border-slate-300 h-3.5 w-3.5" />
                  keep "{k}" on both
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Build the {move_*, copy_*} payload arrays from the selection Sets — one source of truth
// used by BOTH spin-off and split, so the hand-off is assembled identically.
export function buildHandoffPayload(sel, keepNum, selKw, keepKw) {
  return {
    move_number_ids: [...sel].filter((id) => !keepNum.has(id)),
    copy_number_ids: [...sel].filter((id) => keepNum.has(id)),
    move_keywords: [...selKw].filter((k) => !keepKw.has(k)),
    copy_keywords: [...selKw].filter((k) => keepKw.has(k)),
  };
}
